import dayjs from "dayjs";
import i18n from "multilingual";

export const formatDate = (date, separator = "-", format = "MM/DD/YYYY") => {
  if (!date) {
    return "Not Available";
  }
  if (!dayjs(date).isValid()) {
    return "Invalid date";
  }
  return dayjs(date)?.format(format.replaceAll("-", separator));
};

// unixDate function with error handling
export const unixDate = (date) => {
  if (!dayjs(date).isValid()) {
    return "Invalid date";
  }
  return dayjs(date).unix();
};

// epochConverter function with error handling
export const epochConverter = (data) => {
  // Accept several forms: number (seconds or milliseconds), numeric string, or ISO string
  if (data == null) return null;

  // If numeric-string, convert to number
  if (typeof data === "string" && /^\d+$/.test(data)) {
    data = Number(data);
  }

  // If it's a number, detect seconds vs milliseconds
  if (typeof data === "number" && !isNaN(data)) {
    // heuristic: timestamps > 1e12 are milliseconds (e.g., Date.now()), else seconds
    const ms = data > 1e12 ? data : data * 1000;
    return new Date(ms);
  }

  // If it's an ISO/date string, try to parse
  if (typeof data === "string") {
    const d = new Date(data);
    return isNaN(d.getTime()) ? null : d;
  }

  // unknown format
  return null;
};

// isDateEqual function with error handling
export const isDateEqual = (date1, date2) => {
  if (!dayjs(date1).isValid() || !dayjs(date2).isValid()) {
    return "One or both dates are invalid";
  }
  const isEqual =
    dayjs(date1)?.format("MM/DD/YYYY") === dayjs(date2)?.format("MM/DD/YYYY");

  return isEqual;
};

export const formatDateWithOrdinalSuffix = (dateString) => {
  const date = dayjs(dateString);

  function getOrdinalSuffix(day) {
    if (day >= 11 && day <= 13) {
      return "th";
    }
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  }

  const formattedDate = `${date.format("MMM")} ${date.format(
    "D"
  )}${getOrdinalSuffix(date.date())}, ${date.format("YYYY")}`;
  return formattedDate;
};

export function getDurationString(startTimeInput) {
  const startTime = dayjs();
  const endTime = dayjs(startTimeInput);

  const isPast = endTime.isBefore(startTime);

  const durationInMilliseconds = Math.abs(endTime.diff(startTime));

  const days = Math.floor(durationInMilliseconds / (24 * 60 * 60 * 1000));
  const hours = Math.floor(
    (durationInMilliseconds % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000)
  );
  const minutes = Math.floor(
    (durationInMilliseconds % (60 * 60 * 1000)) / (60 * 1000)
  );

  let result = "";

  if (days > 0) {
    result += `${days} ${i18n?.t(`common.${days !== 1 ? "DAYS" : "DAY"}`)} `;
  }

  if (hours > 0) {
    result += `${hours} ${i18n?.t(
      `common.${hours !== 1 ? "HOURS" : "HOUR"}`
    )} `;
  }

  if (minutes > 0) {
    result += `${minutes} ${i18n?.t(
      `common.${minutes !== 1 ? "MINUTES" : "MINUTE"}`
    )} `;
  }

  result = result.trim(); // Remove any trailing whitespace

  if (isPast) {
    return result + " " + i18n?.t("common.AGO");
  } else {
    return result + " " + i18n?.t("common.LEFT");
  }
}

export const dayDifference = (date) => {
  const currentDate = dayjs();
  const endDate = dayjs(date);
  let daysDifference;

  if (isDateEqual(endDate, currentDate)) {
    daysDifference = 0;
  } else if (endDate.diff(currentDate, "day") >= 0) {
    daysDifference = endDate.diff(currentDate, "day") + 1;
  } else {
    daysDifference = endDate.diff(currentDate, "day");
  }

  let timeLeft =
    daysDifference >= 0
      ? daysDifference <= 1
        ? `${daysDifference} ${i18n.t("appointments.DAY_LEFT")}`
        : `${daysDifference} ${i18n.t("dashboard.DAYS_LEFT")}`
      : `${i18n.t("appointments.OVERDUE_BY")} ${daysDifference * -1} ${
          daysDifference === -1 ? i18n.t("common.DAY") : i18n.t("common.DAYS")
        }`;

  if (daysDifference === 0) {
    timeLeft = `${i18n.t("appointments.DUE_TODAY")}`;
  }

  return {
    value: daysDifference,
    string: timeLeft,
  };
};
